import {
	Box,
	Button,
	Container,
	Divider,
	IconButton,
	MenuItem,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import { Add, Autorenew, CheckCircle, Delete, EditOutlined } from "@material-ui/icons";
import DarkDataGrid from "common/components/DarkDataGrid/DarkDataGrid";
import CustomModal from "common/components/CustomModal/CustomModal";
import {
	ModalBody,
	ModalHeader,
	ModalPanel,
} from "common/components/CustomModal/Modal/ModalFrame";
import { SetLoading } from "common/components/Loader/Loader";
import DeleteConfirmModal from "common/components/Modals/DeleteConfirmModal/DeleteConfirmModal";
import RecordInUseModal from "common/components/Modals/RecordInUseModal/RecordInUseModal";
import { GamesEnum } from "common/constants/Games";
import ModalHelper from "common/helpers/modalHelper";
import * as React from "react";
import { useEffect, useState } from "react";
import SeasonService from "services/SeasonService";
import { Colors } from "styles/Colors";
import { Helmet } from "react-helmet";

const GAME_OPTIONS = [
	{ id: GamesEnum.RocketLeague, label: "Rocket League" },
	{ id: GamesEnum.CounterStrike, label: "Counter-Strike" },
];

const gameLabel = (id) =>
	GAME_OPTIONS.find((g) => g.id === id)?.label ?? `Game ${id}`;

// API -> ISO strings. Grid and <input> want yyyy-MM-dd.
const toDateInput = (iso) => (iso ? String(iso).split("T")[0] : "");

const useStyles = makeStyles(() => ({
	titleRow: {
		display: "flex",
		alignItems: "center",
		gap: 20,
	},
	sectionRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 12,
	},
	gridWrap: { height: 520, width: "100%", maxWidth: 1100 },
	dataGrid: {
		border: `1px solid ${Colors.panelLight}`,
		"& .MuiDataGrid-columnHeaders": { borderBottomColor: Colors.panelLight },
	},
	select: {
		minWidth: 200,
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: Colors.panelLighterColor,
		},
		"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: Colors.primaryDarker,
		},
		"& .MuiSelect-nativeInput": { margin: 0, padding: 0, border: 0 },
	},
	actionButton: { color: Colors.primary },
	assignButton: {
		"&.Mui-disabled": {
			backgroundColor: Colors.panelLight,
			color: Colors.primaryDarker,
		},
	},
	orphanRow: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		padding: "8px 0",
		borderBottom: `1px solid ${Colors.panelLight}`,
	},
	orphanName: { flex: 1, minWidth: 0 },
	orphanDates: { color: Colors.primaryDarker, fontSize: 13 },
	modalForm: {
		display: "flex",
		flexDirection: "column",
		gap: 16,
		minWidth: 320,
	},
	modalButtons: {
		display: "flex",
		justifyContent: "flex-end",
		gap: 8,
		marginTop: 16,
	},
}));

const BLANK_SEASON = {
	id: 0,
	gameId: GamesEnum.RocketLeague,
	name: "",
	startDate: "",
	endDate: "",
};

function SeasonEditorModal({ season, onSaved }) {
	const styles = useStyles();
	const [form, setForm] = useState({
		id: season.id ?? 0,
		gameId: season.gameId ?? GamesEnum.RocketLeague,
		name: season.name ?? "",
		startDate: toDateInput(season.startDate),
		endDate: toDateInput(season.endDate),
	});
	const [error, setError] = useState("");

	const save = async () => {
		if (!form.name.trim()) {
			setError("Name is required.");
			return;
		}
		if (!form.startDate || !form.endDate || form.endDate <= form.startDate) {
			setError("End date must be after start date.");
			return;
		}

		SetLoading(true, "Saving season");
		try {
			await SeasonService.saveSeason({ ...form, name: form.name.trim() });
			ModalHelper.closeModal();
			await onSaved();
		} catch {
			// 400s and 5xx surface via axiosWrapper's ErrorModal.
		} finally {
			SetLoading(false);
		}
	};

	return (
		<CustomModal>
			<ModalPanel>
				<ModalHeader onClose={() => ModalHelper.closeModal()}>
					{form.id ? "Edit Season" : "New Season"}
				</ModalHeader>
				<ModalBody>
					<div className={styles.modalForm}>
						<TextField
							label="Name"
							variant="outlined"
							className={styles.select}
							value={form.name}
							onChange={(e) => setForm({ ...form, name: e.target.value })}
							fullWidth
						/>
						<TextField
							select
							label="Game"
							variant="outlined"
							className={styles.select}
							value={form.gameId}
							onChange={(e) =>
								setForm({ ...form, gameId: Number(e.target.value) })
							}
							SelectProps={{ MenuProps: { style: { zIndex: 1600 } } }}
							fullWidth
						>
							{GAME_OPTIONS.map((g) => (
								<MenuItem key={g.id} value={g.id}>
									{g.label}
								</MenuItem>
							))}
						</TextField>
						<TextField
							label="Start date"
							type="date"
							variant="outlined"
							className={styles.select}
							InputLabelProps={{ shrink: true }}
							inputProps={{ style: { colorScheme: "dark" } }}
							value={form.startDate}
							onChange={(e) => setForm({ ...form, startDate: e.target.value })}
							fullWidth
						/>
						<TextField
							label="End date"
							type="date"
							variant="outlined"
							className={styles.select}
							InputLabelProps={{ shrink: true }}
							inputProps={{ style: { colorScheme: "dark" } }}
							value={form.endDate}
							onChange={(e) => setForm({ ...form, endDate: e.target.value })}
							fullWidth
						/>
						{error && (
							<Typography variant="body2" style={{ color: Colors.error }}>
								{error}
							</Typography>
						)}
					</div>
					<div className={styles.modalButtons}>
						<Button onClick={() => ModalHelper.closeModal()}>Cancel</Button>
						<Button variant="contained" color="primary" onClick={save}>
							Save
						</Button>
					</div>
				</ModalBody>
			</ModalPanel>
		</CustomModal>
	);
}

function RowActions({ row, onEdit, onDelete }) {
	const styles = useStyles();
	return (
		<>
			<IconButton
				size="small"
				aria-label="edit"
				className={styles.actionButton}
				onClick={() => onEdit(row)}
			>
				<EditOutlined fontSize="small" />
			</IconButton>
			<IconButton
				size="small"
				aria-label="delete"
				className={styles.actionButton}
				onClick={() => onDelete(row)}
			>
				<Delete fontSize="small" />
			</IconButton>
		</>
	);
}

function OrphanRow({ orphan, seasonOptions, onAssigned }) {
	const styles = useStyles();
	const [seasonId, setSeasonId] = useState("");

	const assign = async () => {
		if (seasonId === "") return;
		SetLoading(true, "Assigning event");
		try {
			await SeasonService.assignEvent(orphan.id, seasonId);
			await onAssigned();
		} finally {
			SetLoading(false);
		}
	};

	return (
		<div className={styles.orphanRow}>
			<div className={styles.orphanName}>
				<div>{orphan.name || `Event ${orphan.id}`}</div>
				<div className={styles.orphanDates}>
					{toDateInput(orphan.startDate) || "?"} &rarr;{" "}
					{toDateInput(orphan.endDate) || "?"}
				</div>
			</div>
			<TextField
				select
				size="small"
				variant="outlined"
				label="Season"
				className={styles.select}
				value={seasonId}
				onChange={(e) => setSeasonId(e.target.value)}
			>
				{seasonOptions.map((s) => (
					<MenuItem key={s.id} value={s.id}>
						{s.name}
					</MenuItem>
				))}
			</TextField>
			<Button
				variant="contained"
				color="primary"
				className={styles.assignButton}
				disabled={seasonId === ""}
				onClick={assign}
			>
				Assign
			</Button>
		</div>
	);
}

export default function SeasonManager() {
	const styles = useStyles();
	const [seasons, setSeasons] = useState(null);
	const [orphanGame, setOrphanGame] = useState(GamesEnum.RocketLeague);
	const [orphans, setOrphans] = useState(null);

	// Handle potential fall back to SPA HTML shell (200) by converting non-arrays to [].
	const asArray = (data) => (Array.isArray(data) ? data : []);
	const fetchSeasons = () =>
		SeasonService.getSeasons().then((data) => setSeasons(asArray(data)));
	const fetchOrphans = (gameId) =>
		SeasonService.getOrphans(gameId).then((data) => setOrphans(asArray(data)));

	useEffect(() => {
		SetLoading(true);
		Promise.all([fetchSeasons(), fetchOrphans(GamesEnum.RocketLeague)]).finally(
			() => SetLoading(false)
		);
		// Fetch once on mount.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const changeOrphanGame = (gameId) => {
		setOrphanGame(gameId);
		SetLoading(true);
		fetchOrphans(gameId).finally(() => SetLoading(false));
	};

	const openEditor = (season) => {
		ModalHelper.openModal(
			<SeasonEditorModal
				season={season}
				onSaved={() => Promise.all([fetchSeasons(), fetchOrphans(orphanGame)])}
			/>
		);
	};

	const removeSeason = (row) => {
		if (row.eventCount > 0 || row.trophyRowCount > 0) {
			ModalHelper.openModal(<RecordInUseModal objectName="Season" />);
			return;
		}
		ModalHelper.openModal(
			<DeleteConfirmModal
				objectName="Season"
				onConfirm={async () => {
					SetLoading(true, "Deleting season");
					try {
						await SeasonService.deleteSeason(row.id);
						await fetchSeasons();
					} catch {
						ModalHelper.openModal(<RecordInUseModal objectName="Season" />);
					} finally {
						SetLoading(false);
					}
				}}
			/>
		);
	};

	const recalculateCurrent = async () => {
		SetLoading(true, "Recalculating leaderboards");
		try {
			await SeasonService.recalculateCurrent();
			await fetchSeasons();
		} finally {
			SetLoading(false);
		}
	};

	const columns = [
		{ field: "name", headerName: "Name", flex: 1, minWidth: 180 },
		{
			field: "gameId",
			headerName: "Game",
			width: 150,
			valueFormatter: (p) => gameLabel(p.value),
		},
		{
			field: "startDate",
			headerName: "Start",
			width: 120,
			valueFormatter: (p) => toDateInput(p.value),
		},
		{
			field: "endDate",
			headerName: "End",
			width: 120,
			valueFormatter: (p) => toDateInput(p.value),
		},
		{
			field: "isCurrent",
			headerName: "Current",
			width: 100,
			renderCell: (p) =>
				p.value ? (
					<CheckCircle fontSize="small" style={{ color: Colors.green }} />
				) : (
					<span />
				),
		},
		{ field: "eventCount", headerName: "Events", width: 90, type: "number" },
		{
			field: "trophyRowCount",
			headerName: "Trophy rows",
			width: 120,
			type: "number",
		},
		{
			field: "actions",
			headerName: "",
			width: 100,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (p) => (
				<RowActions row={p.row} onEdit={openEditor} onDelete={removeSeason} />
			),
		},
	];

	const orphanSeasonOptions = (seasons || []).filter(
		(s) => s.gameId === orphanGame
	);

	return (
		<Container maxWidth={false}>
			<Helmet>
				<title>Pickstop - Season Manager</title>
			</Helmet>

			<Box mt={4} mb={2} className={styles.titleRow}>
				<Typography variant="h5">Season Manager</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<Add />}
					onClick={() => openEditor(BLANK_SEASON)}
				>
					New Season
				</Button>
				<Button
					variant="contained"
					color="primary"
					startIcon={<Autorenew />}
					title="Recalculate the current season's leaderboard for each game"
					onClick={recalculateCurrent}
				>
					Recalculate leaderboards
				</Button>
			</Box>

			<Box className={styles.gridWrap}>
				<DarkDataGrid
					rows={seasons || []}
					columns={columns}
					pageSize={25}
					rowsPerPageOptions={[25, 50, 100]}
					disableSelectionOnClick
					className={styles.dataGrid}
				/>
			</Box>

			<Box mt={5}>
				<Divider />
				<Box mt={3} className={styles.sectionRow}>
					<Typography variant="h6">Orphaned events (no season)</Typography>
					<TextField
						select
						size="small"
						variant="outlined"
						label="Game"
						className={styles.select}
						value={orphanGame}
						onChange={(e) => changeOrphanGame(Number(e.target.value))}
					>
						{GAME_OPTIONS.map((g) => (
							<MenuItem key={g.id} value={g.id}>
								{g.label}
							</MenuItem>
						))}
					</TextField>
				</Box>

				<Box mt={2}>
					{orphans && orphans.length === 0 && (
						<Typography variant="body2" style={{ color: Colors.primaryDarker }}>
							No orphaned events for {gameLabel(orphanGame)}.
						</Typography>
					)}
					{(orphans || []).map((orphan) => (
						<OrphanRow
							key={orphan.id}
							orphan={orphan}
							seasonOptions={orphanSeasonOptions}
							onAssigned={() =>
								Promise.all([fetchSeasons(), fetchOrphans(orphanGame)])
							}
						/>
					))}
				</Box>
			</Box>
		</Container>
	);
}
